///<amd-module name="Core/Medius.Core.Web/Scripts/AdminPages/Company/CompanyTree/companyTree"/>

import * as React from "react";
import { Card, Company, CompanyTreeNode, CompanyTreeView, TreeViewDataMethodEnum } from "@medius/ui-controls";
import { get } from "Core/Medius.Core.Web/Scripts/Medius/core/communication/remediumApi";
import { handleError } from "Core/Medius.Core.Web/Scripts/lib/errorHandling/errorHandler";
import { translate } from "Core/Medius.Core.Web/Scripts/lib/globalization";

interface CompanyTreeResponseNode {
  internalId: number,
  companyId: string,
  globalId: string,
  name: string,
  searchTerm: string,
  isActive: boolean,
  isVirtual: boolean,
  isAccessible: boolean,
  descendants: CompanyTreeResponseNode[]
}

const setCompanyIdInUrl = (id: string) => {
  const currentUrl = window.location.href;
  const updatedUrl = currentUrl.replace(/\/\d+$/, '') + `/${id}`;
  window.location.assign(updatedUrl);
};

const mapToCompanyTreeNode = (descendants: CompanyTreeResponseNode[]): CompanyTreeNode[] => {
  return descendants.map(node => ({
    companyId: node.companyId,
    globalId: node.globalId,
    companyIdentifier: node.internalId.toString(),
    name: node.name,
    filterTerm: node.searchTerm,
    isActive: node.isActive,
    isVirtual: node.isVirtual,
    isAccessible: node.isAccessible,
    descendants: mapToCompanyTreeNode(node.descendants)
  }));
};

export function CompanyTree({ entityId }: { entityId: number | null }) {
  const [companyTree, setCompanyTree] = React.useState<CompanyTreeNode[]>([]);

  const handleSelectedCompanyChanged = (company: Company | undefined) => {
    if (company) setCompanyIdInUrl(company.companyIdentifier);
  };

  const getCompanyTree = async (): Promise<CompanyTreeNode[]> => {
    try {
      const response = await get("remedium-api/companies/tree");
      const companyTreeResponseNode = await response.json();
      const companyTreeNode = mapToCompanyTreeNode(companyTreeResponseNode);

      setCompanyTree(companyTreeNode);
      return companyTreeNode;
    }
    catch (e) {
      handleError(e);
    }
  };

  // Temporary solution
  // TreeView and CompanyTreeView currently throw "Cannot read properties of undefined (reading 'itemKey')"
  // when the provided "selectedItemKey" or "selectedCompanyTreeIdentifier" is not found in the items.
  // TreeView will be refactored to ignore invalid item keys provided in selectedItemKey.
  const isIdentifierValid = (nodes: CompanyTreeNode[], entityId: number): boolean => {
    const filteringSwitch = document.querySelector('[data-testid="company-tree-filtering-switch"]') as HTMLInputElement;

    const validateNode = (nodes: CompanyTreeNode[]): boolean => {
      return nodes.some(node => {
        if (
          node.companyIdentifier === entityId.toString()
          && (node.isActive || filteringSwitch?.checked)
        ) {
          return true;
        }

        return validateNode(node.descendants);
      });
    };

    return validateNode(nodes);
  };

  return (
    <Card height="fill">
      <CompanyTreeView
        description={translate("#Core/company.treeViewDescription")}
        dataMethodType={TreeViewDataMethodEnum.AllItemsAsynchronous}
        getAllItems={getCompanyTree}
        selection={{
          allowMultiple: false,
          selectedCompanyIdentifier: entityId != null && isIdentifierValid(companyTree, entityId) ? entityId.toString() : undefined,
          onSelectedCompanyChanged: handleSelectedCompanyChanged
        }}
        data-testid="company-tree"
      />
    </Card>
  );
}
